import {HoleConfig, Operation} from 'components/customer/Preview3D/types';
import * as THREE from 'three';

export const createHoleMesh = (
    showTexture: boolean,
    holeRadius: number,
    holeDepth: number,
    substrateMaterial?: THREE.MeshStandardMaterial
) => {
    const currentColor: THREE.ColorRepresentation = showTexture
        ? '#636e72'
        : '#00E000';

    // Adjust materials to blend with the environment
    const holeMaterial = new THREE.MeshPhysicalMaterial({
        color: currentColor,
        roughness: 0.5, // Smooth surface appearance
        metalness: 0.1, // Slight metallic effect for a clean finish
        clearcoat: 1.0, // Adds a polished clearcoat to smooth the edges
        clearcoatRoughness: 0.1, // Reduces reflections for a softer look
        flatShading: false, // Smooth shading for better blending
        wireframe: !showTexture,
    });

    let material: THREE.MeshStandardMaterial = holeMaterial;
    if (substrateMaterial && showTexture) {
        material = substrateMaterial.clone();
        material.color = substrateMaterial.color.clone().multiplyScalar(0.5);
    }

    const hole = new THREE.CylinderGeometry(
        holeRadius,
        holeRadius,
        holeDepth,
        64 // Increased segments for smoother geometry,
    );
    const holeMesh = new THREE.Mesh(hole, material);

    const tip = new THREE.CylinderGeometry(
        holeRadius * 0.95,
        holeRadius * 0.95,
        0.1,
        64
    );

    const tipMesh = new THREE.Mesh(tip, material);

    const whiteOutlineMaterial = new THREE.MeshBasicMaterial({
        color: '#ffffff',
        side: THREE.BackSide,
    });

    const blackOutlineMaterial = new THREE.MeshBasicMaterial({
        color: '#000000',
        side: THREE.BackSide,
    });

    const whiteOutlineMesh = new THREE.Mesh(tip.clone(), whiteOutlineMaterial);
    whiteOutlineMesh.scale.multiplyScalar(1.2);

    const blackOutlineMesh = new THREE.Mesh(tip.clone(), blackOutlineMaterial);
    blackOutlineMesh.scale.multiplyScalar(1.1);

    tipMesh.add(whiteOutlineMesh, blackOutlineMesh);

    return [holeMesh, tipMesh];
};

export const useHolePuncher = (
    operation: Operation,
    showTexture: boolean,
    group: THREE.Group,
    holeConfig: HoleConfig
) => {
    const {
        holeRadius,
        holeSpacing,
        holeOffset,
        backsideOffset,
        bottomHoleOffset,
        holeDepth,
    } = holeConfig;

    const panelHeight = Math.abs(
        operation.Segments[0]?.StartCoor.x - operation.Segments[1]?.EndCoor.x
    );
    const panelWidth = Math.abs(
        operation.Segments[0]?.StartCoor.y - operation.Segments[1]?.EndCoor.y
    );

    const secondRowOffset = panelWidth - bottomHoleOffset;

    for (let x = holeOffset; x <= panelHeight - holeOffset; x += holeSpacing) {
        const [bottomHoleMesh, bottomTipMesh] = createHoleMesh(
            showTexture,
            holeRadius,
            holeDepth
        );
        bottomHoleMesh.rotation.x = Math.PI / 2;
        bottomHoleMesh.position.set(x, backsideOffset, -holeDepth / 2);

        bottomTipMesh.rotation.x = Math.PI / 2;
        bottomTipMesh.position.set(x, backsideOffset, 0);

        const [topHoleMesh, topTipMesh] = createHoleMesh(
            showTexture,
            holeRadius,
            holeDepth
        );

        topHoleMesh.castShadow = true;
        topHoleMesh.rotation.x = Math.PI / 2;
        topHoleMesh.position.set(x, secondRowOffset, -holeDepth / 2);

        topTipMesh.rotation.x = Math.PI / 2;
        topTipMesh.position.set(x, secondRowOffset, 0);

        // Add all meshes to the group
        group.add(bottomHoleMesh);
        group.add(bottomTipMesh);
        group.add(topHoleMesh);
        group.add(topTipMesh);
    }
};
